import React, { FC, useState } from 'react'

import styled from 'styled-components'

import { artistClickShareEvent } from 'src/Helpers/TagManager'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import {
  ArtistReviewFeedbackBody,
  usePatchArtistReviewShareMutation,
} from 'src/Redux/artistSpotifyCampaignReviewApi'
import { PrimaryTabs } from 'src/Components/PrimaryTabs'
import { CampaignReview } from 'src/Hooks/useCampaignDetailsReviews'

import {
  ReviewBlock,
  ReviewBlockProps as ReviewBlockComponentProps,
} from '../ReviewBlock'
import { ShareModal } from '../ShareModal'
import { OnFavoriteCuratorParams } from '../../index'

import { ReviewedEmptyState } from './ReviewedEmptyState'
import { NotReviewedBanner } from './NotReviewedBanner'
import { useReviewTabs } from './useReviewTabs'

const MainInfoContainer = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  align-self: stretch;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    box-shadow: none;
    border: none;
    padding: 0;
    margin-top: 32px;
  }
`

type ReviewBlockProps = {
  reviews?: CampaignReview[]
  userId?: number
  campaignId?: number
  trackName?: string
  trackAuthorName?: string
  trackCoverUrl?: string
  onSubmitLikeDislike: (feedbackBody: ArtistReviewFeedbackBody) => void
  onFavoriteCurator: (params: OnFavoriteCuratorParams) => void
  onAddFavoriteCuratorLoading: boolean
  isManualMatching: boolean
}

type ReviewDataToShare = {
  comment: string
  curatorFirstName: string
  curatorLastName: string
  curatorPhotoUrl: string
}

type ShareClickParams = ReviewDataToShare & {
  reviewId: number
}

export const ReviewsList: FC<ReviewBlockProps> = ({
  reviews,
  userId,
  campaignId,
  trackName,
  trackAuthorName,
  trackCoverUrl,
  onSubmitLikeDislike,
  onFavoriteCurator,
  onAddFavoriteCuratorLoading,
  isManualMatching,
}) => {
  const { setSearch } = useCustomSearchParams()
  const [selectedReviewToShare, setSelectedReviewToShare] =
    useState<ReviewDataToShare | null>(null)
  const [shareReview] = usePatchArtistReviewShareMutation()

  const {
    activeTab,
    setActiveTab,
    filteredReviews,
    tabItems,
    isReviewedEmpty,
    showNotReviewedBanner,
  } = useReviewTabs(reviews)

  if (!reviews || reviews.length === 0) {
    return null
  }

  const onShareClick = (review: ShareClickParams): void => {
    setSearch({
      modal: 'share',
    })

    setSelectedReviewToShare({
      comment: review.comment,
      curatorFirstName: review.curatorFirstName,
      curatorLastName: review.curatorLastName,
      curatorPhotoUrl: review.curatorPhotoUrl,
    })

    void shareReview(review.reviewId)

    campaignId &&
      artistClickShareEvent({
        userId,
        campaignId,
      })
  }

  return (
    <MainInfoContainer>
      {isManualMatching && (
        <PrimaryTabs
          items={tabItems}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      )}

      {isManualMatching && isReviewedEmpty && <ReviewedEmptyState />}

      {isManualMatching && showNotReviewedBanner && <NotReviewedBanner />}

      {filteredReviews.map((review) => {
        const reviewBlockProps: ReviewBlockComponentProps = {
          reviewId: review.id,
          comment: review.comment,
          firstName: review.curatorFirstName,
          lastName: review.curatorLastName,
          followersCount: review.followersCount,
          playlistsCount: review.playlistsCount,
          userCover: review.userCover,
          matchedTrackPlaylists: review.matchedTrackPlaylists,
          addedTrackPlaylist: review.addedTrackPlaylist,
          reviewStatus: review.status,
          onShareClick: () =>
            onShareClick({
              reviewId: review.id,
              comment: review.comment || '',
              curatorFirstName: review.curatorFirstName,
              curatorLastName: review.curatorLastName,
              curatorPhotoUrl: review.userCover,
            }),
          reviewComplain: review.reviewComplain,
          vocalRating: review.vocalRating,
          melodyRating: review.melodyRating,
          productionRating: review.productionRating,
          isFavouriteCurator: review.isFavorite,
          onSubmitLikeDislike: review.comment ? onSubmitLikeDislike : void 0,
          onClickSave: review.curatorUserId
            ? () => {
                onFavoriteCurator({
                  curatorUserId: review.curatorUserId ?? 0,
                  isFavorite: Boolean(review.isFavorite),
                })
              }
            : void 0,
          onAddFavoriteCuratorLoading,
        }

        return <ReviewBlock key={review.id} {...reviewBlockProps} />
      })}

      {selectedReviewToShare && (
        <ShareModal
          userId={Number(userId)}
          campaignId={campaignId}
          trackName={trackName}
          trackAuthorName={trackAuthorName}
          trackCoverUrl={trackCoverUrl}
          comment={selectedReviewToShare?.comment}
          firstName={selectedReviewToShare?.curatorFirstName}
          lastName={selectedReviewToShare?.curatorLastName}
          userPhotoUrl={selectedReviewToShare?.curatorPhotoUrl}
        />
      )}
    </MainInfoContainer>
  )
}
